// Performance optimization utilities
import type { PerformanceWithMemory, NavigatorWithConnection, PerformanceMetrics } from '@/types/common';

// Debounce function for search inputs and API calls
export const debounce = <T extends (...args: unknown[]) => void>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// Throttle function for scroll events
export const throttle = <T extends (...args: unknown[]) => void>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Preload critical resources
export const preloadResource = (href: string, as: string, type?: string) => {
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = as;
  link.href = href;
  if (type) link.type = type;
  document.head.appendChild(link);
};

// Prefetch pages that user might visit
export const prefetchPage = (href: string) => {
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = href;
  document.head.appendChild(link);
};

// Lazy load scripts
export const loadScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
};

// Optimize images based on device capabilities
export const getOptimalImageUrl = (
  baseUrl: string,
  width: number,
  quality: number = 85,
  format: 'webp' | 'jpg' | 'png' = 'webp'
): string => {
  // This would integrate with a service like Cloudinary or similar
  // For now, return the base URL
  return baseUrl;
};

// Critical CSS inlining utility
export const inlineCriticalCSS = (css: string) => {
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
};

// Resource hints for better loading performance
export const addResourceHints = () => {
  // DNS prefetch for external domains
  const dnsPrefetchDomains = [
    'fonts.googleapis.com',
    'fonts.gstatic.com',
    'images.unsplash.com',
    'lxlkweksijlrliiauulx.supabase.co'
  ];

  dnsPrefetchDomains.forEach(domain => {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = `//${domain}`;
    document.head.appendChild(link);
  });

  // Preconnect to critical origins
  const preconnectDomains = [
    'https://fonts.googleapis.com',
    'https://lxlkweksijlrliiauulx.supabase.co'
  ];

  preconnectDomains.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = url;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  });
};

// Bundle analyzer helper
export const analyzeBundle = () => {
  if (process.env.NODE_ENV === 'development') {
    // Use proper logging instead of console
    if (typeof window !== 'undefined' && (window as any).logger) {
      (window as any).logger.info('Bundle analysis available in production builds', { 
        component: 'Performance',
        action: 'analyzeBundle' 
      }, { instruction: 'Run: npm run build -- --analyze' });
    }
  }
};

// Performance monitoring
export const measurePerformance = (name: string, fn: () => void) => {
  const start = performance.now();
  fn();
  const end = performance.now();
  
  // Use proper logging instead of console
  if (typeof window !== 'undefined' && (window as any).logger) {
    (window as any).logger.debug(`Performance measurement: ${name}`, { 
      component: 'Performance',
      action: 'measurePerformance' 
    }, { name, duration: end - start });
  }
};

// Memory usage monitoring
export const logMemoryUsage = () => {
  if ('memory' in performance) {
    const memory = (performance as PerformanceWithMemory).memory;
    const memoryData = {
      used: Math.round(memory.usedJSHeapSize / 1048576) + ' MB',
      total: Math.round(memory.totalJSHeapSize / 1048576) + ' MB',
      limit: Math.round(memory.jsHeapSizeLimit / 1048576) + ' MB'
    };
    
    // Use proper logging instead of console
    if (typeof window !== 'undefined' && (window as any).logger) {
      (window as any).logger.info('Memory usage metrics', { 
        component: 'Performance',
        action: 'logMemoryUsage' 
      }, memoryData);
    }
  }
};

// Network quality detection
export const getNetworkQuality = (): string => {
  if ('connection' in navigator) {
    const connection = (navigator as NavigatorWithConnection).connection;
    return connection.effectiveType || 'unknown';
  }
  return 'unknown';
};

// Adaptive loading based on network conditions
export const shouldLoadHighQuality = (): boolean => {
  const networkQuality = getNetworkQuality();
  return networkQuality === '4g' || networkQuality === 'unknown';
};

// Mobile-specific performance utilities
export const isMobileDevice = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export const getTouchCapability = (): boolean => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

export const getDevicePixelRatio = (): number => {
  return window.devicePixelRatio || 1;
};

// Optimize images for mobile devices
export const getMobileOptimizedImageUrl = (
  baseUrl: string,
  containerWidth: number,
  devicePixelRatio: number = getDevicePixelRatio()
): string => {
  const optimalWidth = Math.ceil(containerWidth * devicePixelRatio);
  // This would integrate with image optimization service
  return baseUrl;
};

// Lazy loading intersection observer options for mobile
export const getMobileIntersectionOptions = (): IntersectionObserverInit => {
  return {
    root: null,
    rootMargin: isMobileDevice() ? '50px' : '100px', // Smaller margin on mobile
    threshold: 0.1
  };
};

// Performance metrics specifically for mobile
export const measureMobilePerformance = () => {
  if ('performance' in window) {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    const metrics = {
      // Mobile-specific metrics
      isMobile: isMobileDevice(),
      touchDevice: getTouchCapability(),
      pixelRatio: getDevicePixelRatio(),
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      // Performance metrics
      domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
      loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
      firstByte: navigation.responseStart - navigation.requestStart,
      // Network info if available
      networkType: getNetworkQuality(),
      // Memory info if available
      memory: (performance as PerformanceWithMemory).memory ? {
        used: Math.round((performance as PerformanceWithMemory).memory.usedJSHeapSize / 1048576),
        total: Math.round((performance as PerformanceWithMemory).memory.totalJSHeapSize / 1048576)
      } : null
    };

    return metrics;
  }
  return null;
};

// Preload critical resources for mobile
export const preloadMobileResources = () => {
  // Preload critical fonts
  preloadResource('https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600;700&display=swap', 'style');
  
  // Preload critical images based on device capability
  if (shouldLoadHighQuality()) {
    // Preload high-quality images for fast connections
    preloadResource('/lovable-uploads/2f6c15f0-0e51-4e82-83ca-b7895b7e3f3e.png', 'image');
  }
};

// Touch event optimization
export const optimizeTouchEvents = () => {
  // Add passive event listeners for better scroll performance
  const passiveOptions = { passive: true };
  
  document.addEventListener('touchstart', () => {}, passiveOptions);
  document.addEventListener('touchmove', () => {}, passiveOptions);
  document.addEventListener('touchend', () => {}, passiveOptions);
};

// Initialize mobile optimizations
export const initMobileOptimizations = () => {
  if (isMobileDevice()) {
    // Add mobile-specific CSS class
    document.documentElement.classList.add('mobile-device');
    
    // Optimize touch events
    optimizeTouchEvents();
    
    // Preload mobile resources
    preloadMobileResources();
    
    // Add resource hints
    addResourceHints();
    
    // Set up performance monitoring
    if (process.env.NODE_ENV === 'production') {
      setTimeout(() => {
        const metrics = measureMobilePerformance();
        if (metrics && typeof window !== 'undefined' && (window as any).logger) {
          (window as any).logger.info('Mobile performance metrics captured', { 
            component: 'Performance',
            action: 'measureMobilePerformance' 
          }, metrics);
        }
      }, 2000);
    }
  }
};